import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";

import AiSidebar         from "./AiSidebar";
import AiChatBox         from "./AiChatBox";
import AuthRequiredGate  from "./AuthRequiredGate";
import ConfirmDialog     from "./Confirmdialog";
import CompareBar        from "./CompareBar";
import CompareModal      from "./CompareModal";
import Icon              from "./Icon";

import useTheme             from "./Usetheme";
import useKeyboardShortcuts from "./Usekeyboardshortcuts";
import useGeolocation, { detectNearMeIntent } from "./Usegeolocation";

import LocationPermissionModal from "./LocationPermissionModal";

import { uid, truncateWords }            from "./Helpers";
import { detectRouteIntent }             from "./RouteHelper";
import { ENDPOINTS, KEYBOARD_SHORTCUTS } from "./Constants";

// NOTE: this file's other relative imports ("../../styles/...") go up two
// levels to reach src/, so "../../features/authentication" should land on
// src/features/authentication — verify against your actual folder depth,
// the same way UnifiedSearch.js needed adjusting.
import { sessionStorageService, AUTH_ROUTES, authenticationApi } from "../../features/authentication";

import "../../styles/ai/ai-variables.css";
import "../../styles/ai/ai-layout.css";

function isConversationalFollowUp(text) {
    if (!text) return false;
    const lower = text.toLowerCase();

    if (/(show|find|search|looking\s+for|want\s+a|need\s+a).*(bhk|apartment|villa|property|house|home|flat|project)/.test(lower)) {
        return false;
    }

    if (/(which\s+(one|property)|closer|farther|compare|difference|tell\s+me\s+(more|about)|how\s+(much|many|far)|what\s+is|does\s+it\s+have|is\s+there\s+a)/.test(lower)) {
        return true;
    }

    return false;
}

export default function AiSearchPage() {
    const location        = useLocation();
    const navigate        = useNavigate();
    const initialQuestion = location.state?.question;
    const fromProperty    = location.state?.fromSlug || null;

    const [chats,        setChats]        = useState([]);
    const [activeChatId, setActiveChatId] = useState(null);
    const [loading,      setLoading]      = useState(false);
    const [sidebarOpen,  setSidebarOpen]  = useState(() => window.innerWidth > 640);

    const [compareList,  setCompareList]  = useState([]);
    const [compareOpen,  setCompareOpen]  = useState(false);

    // Whether there's a session at all. Read once at mount — if it changes
    // mid-session (token expires), sendMessage's 401 handling below takes
    // over rather than this recomputing.
    const isAuthenticated = useMemo(() => !!sessionStorageService.get(), []);

    // Single source of truth for "am I logged in": the same token key
    // sessionStorageService reads/writes everywhere else in the app.
    // Previously this looked at "user"/"ogm_user"/"token"/"jwt"/"authToken"
    // in localStorage, none of which is the actual key ("ogm_token"), so
    // this always fell through to the Guest default even when logged in.
    //
    // The JWT only ever encodes email (no name claim), so this JWT-decode
    // is only a placeholder shown before GET /api/auth/me resolves below -
    // without it, everyone's sidebar showed the email's local-part as a
    // fake "name" (e.g. "aravindareddy.lingareddigari@gmail.com" ->
    // "aravindareddy.lingareddig…", truncated and unreadable).
    const [currentUser, setCurrentUser] = useState(() => {
        try {
            const token = sessionStorageService.get();
            if (token) {
                const payload = JSON.parse(atob(token.split(".")[1]));
                const email = payload.email || payload.sub || "";
                return {
                    name:     email.split("@")[0] || "User",
                    email,
                    role:     "Member",
                    avatar:   null,
                    initials: (email || "U").charAt(0).toUpperCase(),
                };
            }
        } catch { /* silent */ }
        return { name: "Guest", email: "", role: "Free Plan", avatar: null, initials: "G" };
    });

    useEffect(() => {
        if (!isAuthenticated) return;
        authenticationApi.getCurrentUser()
            .then((data) => {
                if (!data?.fullName) return;
                setCurrentUser((prev) => ({
                    ...prev,
                    name: data.fullName,
                    email: data.email || prev.email,
                    avatar: data.avatarUrl || prev.avatar,
                    initials: data.fullName.charAt(0).toUpperCase(),
                }));
            })
            .catch(() => { /* keep the JWT-derived placeholder */ });
    }, [isAuthenticated]);

    const [deleteTarget,      setDeleteTarget]      = useState(null);
    const [toast,             setToast]             = useState({ visible: false, text: "" });
    const [locationModalOpen, setLocationModalOpen] = useState(false);
    const [pendingQuery,      setPendingQuery]       = useState("");

    const {
        position, locationName, isGranted, isDenied,
    } = useGeolocation();

    const hasSentInitial         = useRef(false);
    const chatsRef               = useRef([]);
    const { theme, toggleTheme } = useTheme();

    useEffect(() => { chatsRef.current = chats; }, [chats]);

    const activeChat = useMemo(
        () => chats.find((c) => c.id === activeChatId) || null,
        [chats, activeChatId]
    );

    const makeChat = useCallback((title = "New Chat") => ({
        id: uid(), title, messages: [], createdAt: Date.now(),
    }), []);

    /**
     * Persists a chat to the backend, tied to the logged-in account.
     *
     * IMPORTANT: chat.id is a stable, client-only identifier for the
     * lifetime of the chat object - it must NEVER be reassigned. Message
     * sending (see sendMessage below) captures activeChatId in a closure
     * once at the start of an async round-trip and reuses it to append the
     * AI's reply once the response comes back; if the chat's id changed
     * out from under that in-flight closure (which an earlier version of
     * this function did, by rewriting chat.id to the backend's numeric id
     * once the first save landed), the reply would silently fail to match
     * any chat and get dropped - which is exactly what caused "first
     * question gets no answer" whenever the debounced autosave below fired
     * before the AI's response arrived. The backend's row id is tracked
     * separately as chat.backendId instead, used only for update/delete
     * API calls, so chat.id itself never moves.
     */
    const saveChatToBackend = useCallback(async (chat) => {
        if (!chat || chat.messages.length === 0) return;
        if (!sessionStorageService.get()) return;

        const firstUserMsg = chat.messages.find((m) => m.role === "user");
        const payload = {
            title: (chat.title && chat.title !== "New Chat" ? chat.title : firstUserMsg?.text?.slice(0, 40))
                || "AI Search Chat",
            preview: firstUserMsg?.text?.slice(0, 60) || "",
            messages: chat.messages,
        };

        try {
            if (chat.backendId) {
                await authenticationApi.updateChatSession(chat.backendId, payload);
            } else {
                const saved = await authenticationApi.saveChatSession(payload);
                setChats((prev) => prev.map((c) => (c.id === chat.id ? { ...c, backendId: saved.id } : c)));
            }
        } catch {
            // Best-effort - local chat state is unaffected either way.
        }
    }, []);

    const showToast = useCallback((text) => {
        setToast({ visible: true, text });
        setTimeout(() => setToast({ visible: false, text: "" }), 2500);
    }, []);

    const toggleCompare = useCallback((property) => {
        setCompareList((prev) => {
            const exists = prev.find((p) => p.id === property.id);
            if (exists) {
                showToast(`"${property.title}" removed from compare`);
                return prev.filter((p) => p.id !== property.id);
            }
            if (prev.length >= 4) {
                showToast("You can compare up to 4 properties at a time");
                return prev;
            }
            showToast(`"${property.title}" added to compare`);
            return [...prev, property];
        });
    }, [showToast]);

    const clearCompare = useCallback(() => {
        setCompareList([]);
        setCompareOpen(false);
    }, []);

    const createNewChat = useCallback(() => {
        if (activeChat) saveChatToBackend(activeChat);
        const chat = makeChat();
        setChats((prev) => [chat, ...prev]);
        setActiveChatId(chat.id);
    }, [makeChat, activeChat, saveChatToBackend]);

    const renameChat = useCallback((chatId, newTitle) => {
        setChats((prev) => {
            const next = prev.map((c) => (c.id === chatId ? { ...c, title: newTitle } : c));
            const renamed = next.find((c) => c.id === chatId);
            // Only persists once the chat has at least one message -
            // saveChatToBackend no-ops on empty chats, same as autosave.
            if (renamed) saveChatToBackend(renamed);
            return next;
        });
    }, [saveChatToBackend]);

    const requestDeleteChat = useCallback((chatId) => setDeleteTarget(chatId), []);

    const confirmDeleteChat = useCallback(() => {
        if (!deleteTarget) return;

        const target = chatsRef.current.find((c) => c.id === deleteTarget);
        if (target?.backendId && sessionStorageService.get()) {
            authenticationApi.deleteChatSession(target.backendId).catch(() => {});
        }

        setChats((prev) => {
            const next = prev.filter((c) => c.id !== deleteTarget);
            if (activeChatId === deleteTarget) {
                if (next.length > 0) {
                    setActiveChatId(next[0].id);
                } else {
                    const fresh = makeChat();
                    next.push(fresh);
                    setActiveChatId(fresh.id);
                }
            }
            return next;
        });
        setDeleteTarget(null);
    }, [deleteTarget, activeChatId, makeChat]);

    const appendMessage = useCallback((chatId, msg) => {
        setChats((prev) =>
            prev.map((chat) =>
                chat.id === chatId
                    ? { ...chat, messages: [...chat.messages, { ...msg, timestamp: Date.now() }] }
                    : chat
            )
        );
    }, []);

    const editMessage = useCallback((chatId, msgId, newText) => {
        setChats((prev) =>
            prev.map((chat) =>
                chat.id === chatId
                    ? { ...chat, messages: chat.messages.map((m) =>
                            m.id === msgId ? { ...m, text: newText, edited: true } : m) }
                    : chat
            )
        );
    }, []);

    const copyMessageText = useCallback((text) => {
        navigator.clipboard?.writeText(text);
        showToast("Copied to clipboard");
    }, [showToast]);

    const sendMessage = useCallback(
        async (input) => {
            const isObject = input !== null && typeof input === "object";
            const text     = isObject ? input.question?.trim() : input?.trim();
            let userLat    = isObject ? input.userLatitude     : null;
            let userLng    = isObject ? input.userLongitude    : null;
            let userLocName = isObject ? input.userLocationName : null;

            if (!text || !activeChatId || loading) return;

            // Safety net: the AuthRequiredGate branch below already stops
            // an unauthenticated person from ever reaching this component's
            // chat UI, so this only fires if a token that was present at
            // mount gets cleared out from under an active session.
            const token = sessionStorageService.get();
            if (!token) {
                navigate(AUTH_ROUTES.LOGIN, {
                    state: { redirectTo: "/ai-search", pendingQuery: text },
                });
                return;
            }

            const needsLocation = detectNearMeIntent(text);
            const isLocationAgentQuery = /tell me about|how is the area|commute|drive time|travel time|what.*near|schools near|hospital near|compare.*area|rental yield|investment potential|infrastructure|neighborhood/i.test(text);

            const kmChipMatch = text.match(/within\s+\d+\s*km\s+of\s+(.+)/i);
            let explicitLocationRequested = false;

            if (kmChipMatch && kmChipMatch[1]) {
                userLocName = kmChipMatch[1].trim();
                userLat = null;
                userLng = null;
                explicitLocationRequested = true;
            }

            if (!explicitLocationRequested) {
                if (!userLat && isLocationAgentQuery && position) {
                    userLat     = position.latitude;
                    userLng     = position.longitude;
                    userLocName = userLocName ?? position.locationName ?? locationName ?? null;
                }

                if (needsLocation && !userLat) {
                    if (!isDenied && isGranted && position) {
                        userLat = position.latitude;
                        userLng = position.longitude;
                    } else if (!isDenied) {
                        setPendingQuery(text);
                        setLocationModalOpen(true);
                        return;
                    }
                }

                if (!userLat && position) {
                    userLat     = position.latitude;
                    userLng     = position.longitude;
                    userLocName = userLocName ?? position.locationName ?? locationName ?? null;
                }
            }

            const chatId = activeChatId;
            appendMessage(chatId, { id: uid(), role: "user", text });

            setChats((prev) =>
                prev.map((chat) =>
                    chat.id === chatId && chat.title === "New Chat"
                        ? { ...chat, title: truncateWords(text) }
                        : chat
                )
            );

            let apiQuestion = text;
            const msgs = chatsRef.current.find(c => c.id === chatId)?.messages || [];
            const lastAi = [...msgs].reverse().find(m => m.role === "ai" && m.properties?.length > 0);

            if (lastAi && lastAi.properties) {
                const props = lastAi.properties;
                if (props[0] && props[0].location) {
                    apiQuestion = apiQuestion.replace(/(1\s*st|first|above|this)\s+(property|project|home|apartment|villa|building)/gi, props[0].location);
                }
                if (props[1] && props[1].location) {
                    apiQuestion = apiQuestion.replace(/(2\s*nd|second)\s+(property|project|home|apartment|villa|building)/gi, props[1].location);
                }
            }

            const isFollowUp   = isConversationalFollowUp(apiQuestion);

            let isRouteQuery = !!detectRouteIntent(apiQuestion);
            let routeInfo    = isRouteQuery ? detectRouteIntent(apiQuestion) : null;

            if (routeInfo?.usePropAsOrigin) {
                for (let i = msgs.length - 1; i >= 0; i--) {
                    const m = msgs[i];
                    if (m.role === "ai" && m.properties?.length > 0) {
                        const p    = m.properties[0];
                        const pLat = parseFloat(p.latitude ?? p.lat);
                        const pLng = parseFloat(p.longitude ?? p.lng);
                        if (!isNaN(pLat) && !isNaN(pLng)) {
                            routeInfo.origin      = { lat: pLat, lng: pLng };
                            routeInfo.originLabel = p.location || p.title;
                        } else if (p.location) {
                            routeInfo.origin      = p.location;
                            routeInfo.originLabel = p.location || p.title;
                        }
                        break;
                    }
                }
            }

            setLoading(true);

            try {
                const response = await fetch(ENDPOINTS.AI_ASK, {
                    method:  "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization:  `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        question:         apiQuestion,
                        chatId,
                        userLatitude:     userLat  ?? null,
                        userLongitude:    userLng  ?? null,
                        userLocationName: userLocName ?? locationName ?? null,
                        isRouteQuery,
                    }),
                });

                if (response.status === 401) {
                    // Token expired/invalid mid-session — send them to log
                    // back in rather than showing a generic error message.
                    navigate(AUTH_ROUTES.LOGIN, {
                        state: { redirectTo: "/ai-search", pendingQuery: text },
                    });
                    return;
                }

                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const data = await response.json();

                const isLocationAgent = data.isLocationAgent === true;
                if (isLocationAgent) {
                    isRouteQuery = false;
                    routeInfo = null;
                }

                let fetchedProperties = data.properties || [];

                const routeOriginLabel = routeInfo?.originLabel || (
                    typeof routeInfo?.origin === "object" ? "This Location" : routeInfo?.origin || "Unknown"
                );

                const routeText = isRouteQuery
                    ? `🗺️ Showing route from **${routeOriginLabel}** to **${routeInfo.destination}** on the map.\n\nUse the travel mode tabs to switch modes.`
                    : (data.message || "No response received.");

                appendMessage(chatId, {
                    id:           uid(),
                    role:         "ai",
                    text:         routeText,
                    isRouteQuery,
                    isLocationAgent,
                    isRoadSearch: data.isRoadSearch === true,
                    roadName:     data.roadName || null,
                    roadPolyline: data.roadPolyline || null,
                    bufferKm:     data.bufferKm ?? null,
                    hasResults:   isRouteQuery ? false : (fetchedProperties.length > 0),
                    properties:   isRouteQuery ? []    : fetchedProperties,
                    followUps:    isRouteQuery ? []    : (data.followUps   || []),
                });
            } catch (err) {
                console.error("[AiSearchPage] sendMessage error:", err);

                let errorMsg = "Something went wrong. Please try again.";

                if (err.message?.includes("Failed to fetch") || err.message?.includes("NetworkError")) {
                    errorMsg = "Network connection issue. Please check your internet and try again.";
                } else if (err.message?.includes("timeout") || err.message?.includes("timed out")) {
                    errorMsg = "Request timed out. The server might be busy. Please try again in a moment.";
                } else if (err.message?.includes("HTTP 429")) {
                    errorMsg = "Too many requests. Please wait a moment before trying again.";
                } else if (err.message?.includes("HTTP 500") || err.message?.includes("HTTP 502") || err.message?.includes("HTTP 503")) {
                    errorMsg = "Server error. Our team has been notified. Please try again in a few minutes.";
                } else if (err.message?.includes("HTTP 401") || err.message?.includes("HTTP 403")) {
                    errorMsg = "Authentication error. Please log in again.";
                } else if (err.message) {
                    errorMsg = `Error: ${err.message}. Please try again.`;
                }

                appendMessage(chatId, {
                    id: uid(), role: "ai",
                    text: errorMsg,
                    hasResults: false, properties: [], isError: true,
                });
            } finally {
                setLoading(false);
            }
        },
        [activeChatId, loading, appendMessage, isGranted, isDenied, position, locationName, navigate]
    );

    const handleLocationGranted = useCallback((pos) => {
        if (pendingQuery) {
            sendMessage({
                question:         pendingQuery,
                userLatitude:     pos.latitude,
                userLongitude:    pos.longitude,
                userLocationName: pos.locationName ?? null,
            });
            setPendingQuery("");
        }
    }, [pendingQuery, sendMessage]);

    const retryMessage = useCallback((chatId, msgIndex) => {
        const chat = chatsRef.current.find((c) => c.id === chatId);
        if (!chat) return;
        const userMsg = chat.messages.slice(0, msgIndex).reverse().find((m) => m.role === "user");
        if (userMsg) {
            setChats((prev) =>
                prev.map((c) =>
                    c.id === chatId ? { ...c, messages: c.messages.slice(0, msgIndex) } : c
                )
            );
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    sendMessage(userMsg.text);
                });
            });
        }
    }, [sendMessage]);

    const didInit = useRef(false);
    useEffect(() => {
        if (!isAuthenticated) return;
        if (didInit.current) return;
        didInit.current = true;

        authenticationApi.listChatHistory()
            .then((sessions) => {
                const loaded = (sessions || []).map((s) => ({
                    id: uid(),
                    backendId: s.id,
                    title: s.title,
                    messages: s.messages || [],
                    createdAt: new Date(s.updatedAt || s.createdAt).getTime(),
                }));
                const fresh = makeChat();
                setChats([fresh, ...loaded]);
                setActiveChatId(fresh.id);
            })
            .catch(() => createNewChat());
    }, [createNewChat, isAuthenticated, makeChat]);

    // Debounced auto-save: this page has no explicit "close chat" action,
    // so the active chat is saved a little after each change instead -
    // saveChatToBackend itself decides create-vs-update based on chat.id.
    useEffect(() => {
        if (!isAuthenticated || !activeChat || activeChat.messages.length === 0) return;
        const timer = setTimeout(() => saveChatToBackend(activeChat), 1500);
        return () => clearTimeout(timer);
    }, [activeChat, isAuthenticated, saveChatToBackend]);

    useEffect(() => {
        if (!isAuthenticated) return;
        if (initialQuestion && activeChatId && !hasSentInitial.current) {
            hasSentInitial.current = true;
            sendMessage(initialQuestion);
        }
    }, [initialQuestion, activeChatId, sendMessage, isAuthenticated]);

    useKeyboardShortcuts(useMemo(() => ({
        [KEYBOARD_SHORTCUTS.NEW_CHAT]:       createNewChat,
        [KEYBOARD_SHORTCUTS.TOGGLE_SIDEBAR]: () => setSidebarOpen((v) => !v),
        [KEYBOARD_SHORTCUTS.TOGGLE_THEME]:   toggleTheme,
    }), [createNewChat, toggleTheme]));

    // Not logged in: show an in-page "sign in to continue" card instead of
    // bouncing straight to the login form with no context. This intentionally
    // sits after every hook above so hook order stays stable across renders.
    if (!isAuthenticated) {
        return (
            <div className="ai-layout">
                <AuthRequiredGate pendingQuery={initialQuestion} redirectTo="/ai-search" />
            </div>
        );
    }

    return (
        <div className="ai-layout">

            <LocationPermissionModal
                open={locationModalOpen}
                onClose={() => { setLocationModalOpen(false); setPendingQuery(""); }}
                onLocationGranted={handleLocationGranted}
                queryText={pendingQuery}
            />

            {sidebarOpen && (
                <div
                    className="ai-sidebar-backdrop"
                    onClick={() => setSidebarOpen(false)}
                    aria-hidden="true"
                />
            )}

            <AiSidebar
                chats={chats}
                activeChatId={activeChatId}
                setActiveChatId={setActiveChatId}
                createNewChat={createNewChat}
                onRenameChat={renameChat}
                collapsed={!sidebarOpen}
                user={currentUser}
                theme={theme}
                onToggleTheme={toggleTheme}
            />

            <main className="ai-main">
                <div className="ai-topbar">
                    <button className="topbar-btn"
                            onClick={() => setSidebarOpen((v) => !v)}
                            title={sidebarOpen ? "Hide sidebar (Ctrl+B)" : "Show sidebar (Ctrl+B)"}
                            aria-label="Toggle sidebar">
                        <Icon name="sidebar" size={16} />
                    </button>

                    <div
                        className="topbar-logo"
                        onClick={() => fromProperty ? navigate(`/property/${fromProperty}`) : navigate("/")}
                        title={fromProperty ? "Back to property" : "Go to homepage"}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => e.key === "Enter" && (fromProperty ? navigate(`/property/${fromProperty}`) : navigate("/"))}
                        aria-label={fromProperty ? "Back to property" : "Go to homepage"}
                    >
                        <img src="/logo.png" alt="OGM" className="topbar-logo-img" />
                        <div className="topbar-logo-text">
                            <span className="topbar-logo-name">One Global Marketplace</span>
                        </div>
                    </div>

                    <span className="topbar-title">{activeChat?.title || "New Chat"}</span>
                    <div className="topbar-spacer" />
                    <button className="topbar-btn" onClick={toggleTheme}
                            title="Toggle theme (Ctrl+J)" aria-label="Toggle theme">
                        <Icon name={theme === "dark" ? "sun" : "moon"} size={16} />
                    </button>
                </div>

                <AiChatBox
                    chat={activeChat}
                    loading={loading}
                    onSend={sendMessage}
                    onEditMessage={editMessage}
                    onRetry={retryMessage}
                    onCopy={copyMessageText}
                    userPosition={position}
                    compareList={compareList}
                    onCompare={toggleCompare}
                />
            </main>

            <div className={`toast ${toast.visible ? "visible" : ""}`}
                 role="status" aria-live="polite">
                {toast.text}
            </div>

            <CompareBar
                count={compareList.length}
                properties={compareList}
                onClick={() => setCompareOpen(true)}
                onClear={clearCompare}
            />

            {compareOpen && (
                <CompareModal
                    properties={compareList}
                    onClose={() => setCompareOpen(false)}
                    onRemove={(id) => setCompareList((prev) => prev.filter((p) => p.id !== id))}
                />
            )}

            {deleteTarget && (
                <ConfirmDialog
                    title="Delete Chat"
                    message="This conversation and all its messages will be permanently removed."
                    confirmLabel="Delete"
                    onConfirm={confirmDeleteChat}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
}